import { useState, useCallback } from 'react';
import { validateName } from '@saga/shared';

export interface UseNameValidationReturn {
    value: string;
    error: string;
    setValue: (value: string) => void;
    handleChange: (value: string) => void;
    validate: () => boolean;
    clearError: () => void;
    reset: () => void;
}

export const useNameValidation = (initialValue: string = ''): UseNameValidationReturn => {
    const [value, setValue] = useState(initialValue);
    const [error, setError] = useState('');

    const handleChange = useCallback((newValue: string) => {
        setValue(newValue);
        // Clear error when user starts typing (but don't validate yet)
        setError('');
    }, []);

    const validate = useCallback(() => {
        const validation = validateName(value);
        setError(validation.isValid ? '' : validation.error || '');
        return validation.isValid;
    }, [value]);

    const clearError = useCallback(() => {
        setError('');
    }, []);

    const reset = useCallback(() => {
        setValue('');
        setError('');
    }, []);

    const setValueOnly = useCallback((newValue: string) => {
        setValue(newValue);
        setError(''); // Clear any existing errors when setting value
    }, []);

    return {
        value,
        error,
        setValue: setValueOnly,
        handleChange,
        validate,
        clearError,
        reset,
    };
};
